import React, { useState } from 'react';
import { CloudIcon } from 'lucide-react';
import { DomainHeader } from '../../components/ui/DomainHeader';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { RegistrationPanel } from '../../components/academics/RegistrationPanel';
import { TimetableGrid } from '../../components/academics/TimetableGrid';
import { AssignmentBoard } from '../../components/academics/AssignmentBoard';
import { AttendancePanel } from '../../components/academics/AttendancePanel';
import { ResultsPanel } from '../../components/academics/ResultsPanel';
import { ResourcesPanel } from '../../components/academics/ResourcesPanel';
import { LearningPanel } from '../../components/learning/LearningPanel';
import { useAccount } from '../../contexts/AccountContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { currentSemester } from '../../data/academics';
const TABS = ['Registration', 'Timetable', 'Learning', 'Assignments', 'Attendance', 'Results', 'Materials'] as const;
export function Academics() {
  const {
    account,
    institutionName
  } = useAccount();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Registration');
  const {
    isNew
  } = useDataMode();
  if (isNew) {
    return <FirstRun surface="Academics" what="Your courses, timetable, deadlines and results in one place." why="Register for the session, see the week you actually have, keep assignment deadlines and attendance in view, and hold your results and course materials together instead of across five different portals." note="Campuzon is not your university's records system. Anything marked as published by your institution came from them; anything you enter yourself is labelled as yours, and Campuzon never presents its own figures as a university grade." steps={[{
      label: 'Verify your academic identity first',
      detail: 'Registration, results and course spaces need your institution and programme confirmed, not just declared.',
      to: '/identity'
    }, {
      label: 'Register for the session',
      detail: 'Once verified, your programme’s courses and unit limits appear here and registration opens.'
    }, {
      label: 'Add what you already know',
      detail: 'You can enter your own timetable and deadlines now — they are marked as entered by you.'
    }]} data-id="element-9978" data-mp="1mjg9k71zz6lw" />;
  }
  return <div className="space-y-6" data-id="element-9979" data-mp="1gkl5gbcknp0f">
      <DomainHeader domain="academics" eyebrow={currentSemester} title="Academics" description={`${account.academic.programme} · ${account.academic.department}, ${account.academic.faculty}`} actions={<>
            <Badge tone="brand" data-id="element-9981" data-mp="j3mgr2xvivpk">{account.academic.level}</Badge>
            <Badge data-id="element-9982" data-mp="2us10u7wb6m5b">{account.academic.status}</Badge>
          </>} data-id="element-9980" data-mp="1glml6t4j8zoh" />

      <div className="flex flex-col gap-3 rounded-card border border-line bg-surface px-4 py-3 sm:flex-row sm:items-center" data-id="element-9983" data-mp="2upy6jbo4i6us">
        <CloudIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-9984" data-mp="2ku3ejpy0ezfc" />
        <p className="text-[13px] leading-5 text-ink-muted" data-id="element-9985" data-mp="2krqo0oe6rgqo">
          <span className="font-medium text-ink" data-id="element-9986" data-mp="2ptg3cg8fygkk">Directory sync only.</span> {institutionName} shares
          its course catalogue with Campuzon, so course codes, lecturers and capacity are official.
          Your grades, attendance and submissions are yours to record until the university enables full
          integration.
        </p>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Academic sections" data-id="element-9987" data-mp="38j5yx5k136dj" />

      <div role="tabpanel" aria-label={tab} data-id="element-9988" data-mp="38l0hid7tumeg">
        {tab === 'Registration' && <RegistrationPanel data-id="element-9989" data-mp="1t0ue9wnf9s7m" />}
        {tab === 'Timetable' && <TimetableGrid data-id="element-9990" data-mp="1x4ijw9shudq8" />}
        {tab === 'Learning' && <LearningPanel data-id="element-9991" data-mp="1x5bvbo2edrep" />}
        {tab === 'Assignments' && <AssignmentBoard data-id="element-9992" data-mp="2dbngiiw4ful3" />}
        {tab === 'Attendance' && <AttendancePanel data-id="element-9993" data-mp="2dcgg8zwnxjtq" />}
        {tab === 'Results' && <ResultsPanel data-id="element-9994" data-mp="28jx3g55suspz" />}
        {tab === 'Materials' && <ResourcesPanel data-id="element-9995" data-mp="28j3y98mhob9y" />}
      </div>
    </div>;
}